<script setup lang="ts">
import { generateTranslationKey } from '~/utilities/data-transformers';
import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';
import type { IElement } from '~/interfaces/form/formBuilder';
import BuilderInputPrefix from '~/components/user-interface/dynamic-form/builder-input-prefix.vue';
import type { socialIcons } from '~/interfaces/components/icons';
const props = defineProps<{
  element: IElement;
  formErrors: { [key: string]: string };
}>();
const inputRef = ref<typeof InputText>();
const model = defineModel<string>();
const emits = defineEmits<{
  (e: 'inputBlur'): void;
  (e: 'inputChange', id: string): void;
  (e: 'update:modelValue', value: string): void;
}>();
const site = useSiteStore();
const inputMode = computed(() =>
  props.element?.inputType === 'tel'
    ? 'numeric'
    : props.element?.inputType === 'email'
      ? 'email'
      : props.element?.inputType,
);
const internalModel = ref('');
const sanitiseValue = (value: string) => {
  if (props.element.inputType !== 'tel') return value;
  return value.replace(/\D/g, '');
};
const handleValueChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const sanitisedValue = sanitiseValue(input.value);
  if (input.value !== sanitisedValue) input.value = sanitisedValue;
  internalModel.value = sanitisedValue;
  emits('update:modelValue', sanitisedValue);
  emits('inputChange', props.element.id);
};
const touched = ref<boolean>(false);
onMounted(() => {
  internalModel.value = sanitiseValue(model.value ?? '');
});
watch(
  () => model.value,
  () => {
    internalModel.value = sanitiseValue(model.value ?? '');
  },
);
</script>
<template>
  <FloatLabel
    v-if="!!element"
    :id="`${element.id.replace(' ', '')}-label`"
    :input-type="element.inputType"
    :disabled="element.disabled"
    :touched="touched || !!internalModel"
    variant="in"
    :data-prefix="!!element?.prefix"
  >
    <InputText
      :id="element.id"
      ref="inputRef"
      :value="internalModel"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
      :type="element.inputType"
      :input-mode="inputMode"
      :aria-label="element.label"
      :required="element.required"
      :disabled="element.disabled"
      :invalid="!!formErrors[element.id]"
      :name="element.id"
      :touched="touched || !!internalModel"
      :placeholder="
        touched
          ? $t(
              `placeholder.${generateTranslationKey(element.placeholder)}-${site.getRegionCode}`,
              `placeholder.${generateTranslationKey(element.placeholder)}`,
            )
          : ''
      "
      @input="handleValueChange($event)"
      @click="touched = true"
      @focus="touched = true"
      @change="handleValueChange($event)"
      @blur="((model = model?.trim()), emits('inputBlur'), (touched = false))"
    />
    <span
      v-if="element.inputType === 'tel'"
      class="absolute text-base-priority left-0 top-0 border-r p-3 rounded-l-lg stacked-border-color"
      :class="{
        '!text-dark-800/30 dark:!text-light-700/40': element.disabled,
      }"
    >
      {{ site.getDialingCode }}
    </span>
    <BuilderInputPrefix
      v-if="element.inputType === 'tel' || !!element?.prefix"
      v-bind="{
        ...(element.inputType === 'tel' && { type: 'dialing-code' }),
        ...(!!element?.prefix &&
          element.prefix.includes('social') && {
            type: 'social-icon',
            icon: element?.prefix.replace('icon.socials.', '') as socialIcons,
          }),
      }"
    />
    <slot name="suffix" />
    <label :for="element.id">
      {{
        $t(
          `label.${generateTranslationKey(element.label)}-${site.getRegionCode}`,
          `label.${generateTranslationKey(element.label)}`,
        )
      }}
    </label>
  </FloatLabel>
</template>
